import type { LocalRepository } from '@ims/local'
import type { Entitlements, SyncState, TableName, TenantSettingsRow } from '@ims/data'
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
  useSyncExternalStore,
  type DependencyList,
} from 'react'
import { useDateChoice, withDateChoice } from './lib/calendar.ts'
import { customFields, enabledGroups } from './lib/catalog.ts'
import { useSearchParams } from 'react-router'
import { useRepo, useStore, useWorkspace } from './workspace.tsx'

export interface Live<T> {
  data: T | undefined
  loading: boolean
  error: Error | null
  reload(): void
}

/**
 * Runs a local query and re-runs it whenever one of `tables` changes (own
 * writes, pulls from other devices). Without `tables`, any change re-runs it.
 * `deps` must be JSON-serialisable (ids, filters, search text).
 */
export function useLive<T>(
  query: (repo: LocalRepository) => Promise<T>,
  deps: DependencyList,
  tables?: TableName[],
): Live<T> {
  const repo = useRepo()
  const key = JSON.stringify(deps)
  const tableKey = tables ? tables.join(',') : '*'
  const [tick, setTick] = useState(0)
  const [state, setState] = useState<{
    key: string | null
    data: T | undefined
    error: Error | null
  }>({
    key: null,
    data: undefined,
    error: null,
  })
  const runQuery = useEffectEvent(() => query(repo))

  useEffect(() => {
    let active = true
    let version = 0
    const watched = tableKey === '*' ? null : tableKey.split(',')
    const run = () => {
      const mine = ++version
      runQuery().then(
        (data) => {
          if (active && mine === version) setState({ key, data, error: null })
        },
        (error: unknown) => {
          if (active && mine === version) {
            setState((s) => ({
              key,
              data: s.data,
              error: error instanceof Error ? error : new Error(String(error)),
            }))
          }
        },
      )
    }
    run()
    const unsubscribe = repo.subscribe((changed) => {
      if (!watched || changed.some((t) => watched.includes(t))) run()
    })
    return () => {
      active = false
      unsubscribe()
    }
  }, [repo, key, tableKey, tick])

  const reload = useCallback(() => setTick((t) => t + 1), [])
  // Previous data stays visible while a new query (e.g. next page) loads.
  return { data: state.data, loading: state.key !== key, error: state.error, reload }
}

/** The time now, refreshed every half minute (for "5m ago" and things that expire). */
export function useNow(): Date {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30_000)
    return () => clearInterval(timer)
  }, [])
  return now
}

/**
 * The business's settings. `date_display` is how this device shows dates (the account
 * menu's Dates switch); the business's own value is `businessDateDisplay(settings)`.
 */
export function useSettings(): TenantSettingsRow | null | undefined {
  const row = useLive((repo) => repo.settings(), [], ['tenant_settings']).data
  const choice = useDateChoice()
  return useMemo(() => (row ? withDateChoice(row, choice) : row), [row, choice])
}

/**
 * Restaurant mode and business type for the screens' shape (sidebar, routes, tabs). The
 * synced settings row once it is here; before that (a first sync) the business list's copy,
 * fetched at sign-in, so a restaurant never opens on a shop's sidebar. `known` is false
 * only when neither has arrived.
 */
export function useBusinessMode(): {
  restaurant: boolean
  /** A restaurant whose products are its menu (business type restaurant). */
  menu: boolean
  businessType: TenantSettingsRow['business_type'] | null
  known: boolean
} {
  const settings = useSettings()
  const early = useWorkspace().current?.tenant.settings
  const mode = settings ?? early
  const restaurant = mode?.restaurant ?? false
  return {
    restaurant,
    menu: restaurant && mode?.business_type === 'restaurant',
    businessType: mode?.business_type ?? null,
    known: !!mode,
  }
}

/** Batch number and expiry for the batches named by some lines (for printing). */
export function useBatchLabels(
  batchIds: readonly (string | null | undefined)[],
): Record<string, { batch_no: string; expiry_date: string | null }> {
  const ids = [...new Set(batchIds.filter((id): id is string => !!id))].sort()
  return (
    useLive(
      async (repo) => {
        const out: Record<string, { batch_no: string; expiry_date: string | null }> = {}
        for (const id of ids) {
          const b = await repo.get('batches', id)
          if (b) out[id] = { batch_no: b.batch_no, expiry_date: b.expiry_date }
        }
        return out
      },
      [ids.join(',')],
      ['batches'],
    ).data ?? {}
  )
}

/** The product field groups and custom fields this business shows. */
export function useFieldGroups() {
  const settings = useSettings()
  return useMemo(
    () => ({
      groups: enabledGroups(settings),
      custom: customFields(settings),
      loaded: settings !== undefined,
    }),
    [settings],
  )
}

/**
 * The business name to show. Local settings first (a rename shows at once,
 * even offline); the membership's tenant name before settings have loaded.
 */
export function useBusinessName(fallback: string | undefined): string {
  const settings = useSettings()
  return settings?.business_name?.trim() || fallback || 'Sano Godam'
}

/** The last entitlement snapshot (plan, limits, grace); refreshed on every pull. */
/**
 * Whether the plan (or the admin, for this business) gives a switch such as
 * `purchase_control`: on unless the limits say 0. Off until the entitlements load.
 */
export function useFeature(key: string): boolean {
  const limits = useEntitlements()?.limits
  return limits !== undefined && limits[key] !== 0
}

export function useEntitlements(): Entitlements | null {
  const { engine } = useStore()
  const { lastPulledAt } = useSyncState()
  const [value, setValue] = useState<Entitlements | null>(null)
  useEffect(() => {
    let active = true
    void engine.cachedEntitlements().then((v) => active && setValue(v))
    return () => {
      active = false
    }
  }, [engine, lastPulledAt])
  return value
}

export function useSyncState(): SyncState {
  const { engine } = useStore()
  return useSyncExternalStore(
    (onChange) => engine.subscribe(onChange),
    () => engine.state(),
  )
}

export function useOnline(): boolean {
  return useSyncExternalStore(
    (onChange) => {
      window.addEventListener('online', onChange)
      window.addEventListener('offline', onChange)
      return () => {
        window.removeEventListener('online', onChange)
        window.removeEventListener('offline', onChange)
      }
    },
    () => navigator.onLine,
  )
}

/** Wraps an async action with a busy flag and error capture. */
export function useAction<A extends unknown[]>(
  action: (...args: A) => Promise<void>,
  onError: (message: string) => void,
) {
  const [busy, setBusy] = useState(false)
  const run = useCallback(
    async (...args: A) => {
      setBusy(true)
      try {
        await action(...args)
      } catch (error) {
        onError(errorMessage(error))
      } finally {
        setBusy(false)
      }
    },
    [action, onError],
  )
  return [run, busy] as const
}

/** Human message for CoreError / SyncError / Supabase errors ('code: detail'). */
export function errorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error)
  const detail = /^[a-z_]+: (.*)$/s.exec(message)?.[1]
  return detail ? detail.charAt(0).toUpperCase() + detail.slice(1) : message
}

/**
 * A one-shot request in the URL, e.g. "/products?new=1" from the command
 * palette. Returns whether it is present and a function that removes it.
 */
export function useUrlAction(name: string): [boolean, () => void] {
  const [params, setParams] = useSearchParams()
  const clear = () =>
    setParams(
      (p) => {
        p.delete(name)
        return p
      },
      { replace: true },
    )
  return [params.has(name), clear]
}
